import {
	Box,
	Button,
	Card,
	Flex,
	Text,
	useToast
} from "@chakra-ui/react";
import { SmallText } from "components/text/TextTypes";
import {
	ClockCounterClockwise,
	Copy,
	FadersHorizontal,
	Files,
	ShareFat,
	Sparkle,
	Trash
} from "@phosphor-icons/react";
import {
	AGENT_CANVAS,
	apiGetThread,
	apiGetThreads,
	get_json_content,
	setAIContext,
	updateAIContext,
} from "api/ai/ai.service";
import LifecycleState from "api/common/LifecycleState";
import reportDocService, { baseIdFromTypedId } from "api/issb/reportdoc.service";
import LocalSpinner from "components/LocalSpinner";
import PageHeader from "components/text/PageHeader";
import { routes } from "config/paths";
import { useAuth } from "context";
import DevOnly from "context/Auth/DevOnly";
import { useConfirmation } from "context/Mutation/ConfirmationDialogContext";
import _ from "lodash";
import StatusToggle from "pages/ISSB/IssbDataInput/components/StatusToggle";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useRerender } from "rerenderer";
import { colors } from "theme/foundations/colors";
import { formatDate, showSuccessToast } from "utils";
import { copyToClipboard, toTitleCase } from "utils/text-utils";
import AIChatWidget from "./components/AIAssistPanel/AIChatWidget";
import { useContextualPrompts } from "hooks/query/prompt";
import RichTextEditor from "./IssbDataInput/components/RichTextEditor/RichTextEditor";
// import { Card } from "components/Card";
import { FileInfoSpec, getWeaviateQuestion, displayThreadStatus } from "api/ai/ai.service";
import questionService from "api/issb/question.service";
import questionnaireService from "api/issb/questionnaire.service";
import { AButton } from "components/A";
import { RightSidebarHeader } from "components/Layout/RightSidebarWidget";
import { H3 } from "components/text/headings";
import { useRightSidebar } from "context/RightSidebarContext";
import { FaArrowLeft } from "react-icons/fa";
import SearchString from "search-string";
import QuestionCard, { QCardBody, QCardHeader } from "./IssbDataInput/components/QuestionCard";
import api from "api/user-administration/user-administration";
import { standardiseHtml, applyFindReplace } from "./ISSB-helpers";
import CitationSidePanel from './CitationSidePanel';

/**
 *
 * @param {ReportDoc} reportDoc
 * @returns {FileInfoSpec}
 */
export function getFileinfoSpec(reportDoc) {
  const allIds = reportDoc?.sourcesAllowed || [];
  const questionIds = allIds.filter((tid) => tid?.startsWith("Question:")).map((tid) => baseIdFromTypedId(tid));
  const roundIds = allIds.filter((tid) => !tid?.startsWith("Question:")).map((tid) => baseIdFromTypedId(tid));
  const spec = {
    round_ids: roundIds,
    question_filter: reportDoc?.questionFilter || "*",
  };
  if (questionIds.length > 0) {
    spec.question_ids = questionIds;
  }
  return spec;
}

/**
 * Number citations to avoid multiple [1]s and to use the same number for the same citation
 * @param {string} content
 * @returns {string} content
 */
function applyCitationCounter(content) {
  let num4cite = {};
  // edit all citations
  content = content.replace(/<cite(.*?)>(.+?)<\/cite>/g, (match, p1, p2) => {
    const citeKey = p1;
    let num = num4cite[citeKey];
    if (!num) {
      num = Object.keys(num4cite).length + 1;
      num4cite[citeKey] = num;
    }
    const after = `<cite${p1}>[${num}]</cite>`;
    // console.log("applyCitationCounter", "before", match, "after", after);
    return after; // Keep attributes. drop the original [n] and replace with [counter]
  });
  return content;
}

function getAIId(threadId, message) {
  return threadId + ":" + message.output_id + ":" + message.trace_id;
}

/**
 * The main canvas editor component
 * @param {string} ReportDocId this will be reloaded from the server when the thread changes
 * @returns
 */
export default function ReportDocEditorPanel({ reportDocId }) {
	const queryClient = useQueryClient();
	const { getAccessTokenSilently } = useAuth();
	const { prompts: contextualPrompts } = useContextualPrompts({ location: "report-doc-chat" });
	const docQueryKey = ["reportDocEditor", reportDocId]; // Note: different query-key to ensure the editor does load fresh from the server. Fixes white-canvas bug, early Dec 2025
	const { data: reportDoc } = useQuery({
		queryKey: docQueryKey, 
		queryFn: async () => {
			await getAccessTokenSilently();
			return reportDocService.get(reportDocId);
		},
		onSuccess: (reportDoc) => {
			console.log("reportDoc loaded", reportDoc);
		}
	});
	
  const { rerender } = useRerender();
  // fix the sources to be the answers
  const fileinfoSpec = getFileinfoSpec(reportDoc);
  useEffect(() => {
    // console.log("AI context set to reportDoc?.content", reportDoc?.content);
    setAIContext({
      canvasText: standardiseHtml(reportDoc?.content || ""),
    });
  }, [reportDoc?.content]);

  // create a debounced update function to avoid excessive server calls during user input
  const debouncedUpdate = useMemo(
    () =>
      _.debounce((axiosConfig) => {
        // HACK if there is an h1 tag in newContent, set the report title
        const h1 = reportDoc.content?.match(/<h1>(.*?)<\/h1>/);
        if (h1 && h1[1].trim()) {
          console.log("HACK: Setting report title from h1 tag to", h1[1]);
          reportDoc.title = h1[1].trim();
        }
        return reportDocService.update(reportDoc.id, reportDoc, axiosConfig);
      }, 1000),
    [reportDoc]
  );

  const onUserEditsCanvas = (value) => {
    // adjust citation numbers to avoid multiple [1]s
    let newContent = applyCitationCounter(value);
	// standardise html formatting
	newContent = standardiseHtml(newContent);
    // set the new content
    reportDoc.content = newContent;
    // let the AI know
    updateAIContext({
      canvasText: standardiseHtml(newContent),
    });
    // HACK if the report somehow got stuck with state=processing, set it to draft (issue seen Sep 2025)
    if (reportDoc.state === LifecycleState.PROCESSING) {
      console.warn("(fix) Report doc got stuck with state=processing, setting to draft", reportDoc);
      reportDoc.state = LifecycleState.DRAFT;
    }
    // save to server
    debouncedUpdate();
  };

  const [threadId, setThreadId] = useState(null);
  useEffect(() => {
	if (!reportDoc?.id) return;
    // get thread(s) with this report doc id
    apiGetThreads({ reportDocId: reportDoc.id }).then((threads) => {
      if (threads.length) {
        setThreadId(threads[0].id);
      }
    });
  }, [reportDoc?.id, setThreadId]);

  // process canvas_edit response - reload the report doc from the server for the latest version
  const onChatResponse = (response) => {
	queryClient.invalidateQueries(docQueryKey);
    console.log("onChatResponse", response);
  };

  const onThreadUpdate = (thread) => {
    console.log("onThreadUpdate", thread);
	queryClient.invalidateQueries(["reportDoc", reportDocId]);
  };

  // HACK make the editor fit the screen height
  // max height to the bottom of the screen
  const [heightToBottom, setHeightToBottom] = useState("100%");
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      const top = ref.current.getBoundingClientRect().top;
      const maxHeight = window.innerHeight - top;
      console.log(
        "maxHeight",
        maxHeight,
        "from top",
        top,
        "window.innerHeight",
        window.innerHeight,
        "rect",
        ref.current.getBoundingClientRect()
      );
      setHeightToBottom(maxHeight);
    }
  }, [setHeightToBottom, ref]);

  // citations
  const { setRightSidebar, doOpen } = useRightSidebar();
  const openSidebar = (citation, domElement) => {
    setRightSidebar(<CitationSidePanel citation={citation} domElement={domElement} reportDoc={reportDoc} />);
    doOpen();
  };

  // VARIABLE TO TOGGLE BETWEEN UPDATING WITH THE EDIT HISTORY.
  const [isEditHistoryOn, setIsEditHistoryOn] = useState(false);
  const [previousReportDocContent, setPreviousReportDocContent] = useState(reportDoc?.content || "");

  // To change the reportDoc contents to the current previousReportDocContent.
  const handleRevertReportDocChanges = ()=>{
	onUserEditsCanvas(previousReportDocContent);
	rerender();
  }

  return (
    <Flex direction="row" gap={4} height={heightToBottom} ref={ref}>
      <Box
        width="35%"
        // height="100%"
        height={heightToBottom}
        overflow={"hidden"}
        pl={6}
      >
        <HeaderButtons />
        {!isEditHistoryOn && 
          <>
          <PageHeader
            icon={<Sparkle weight="fill" size={32} color={colors.navIconLime} />}
            title="AI Insights" /* Note: figma is inconsistent on AI Insights vs Insights AI */
            titleSize="heading.l"
          />
          <SourcesAndFilters reportDoc={reportDoc} />
          <AIChatWidget
            cannotAddSource={true}
            fileinfoSpec={fileinfoSpec}
			reportdoc_id={reportDocId}
            agent={AGENT_CANVAS}
            threadId={threadId}
            setThreadId={setThreadId}
            onChatResponse={onChatResponse}
			onThreadUpdate={onThreadUpdate}
            height={heightToBottom - 200} // HACK set height to get scrolling on overflow, -50px for the SourcesAndFilters
            contextualPrompts={contextualPrompts}
          />
          </>
        }
        {
          isEditHistoryOn && 
          <Box
            boxShadow="0px 4px 10px rgba(0,0,0,0.1)"
            width="100%"
            p={0}
            display='flex'
            flexDirection='column'
            gap={5}
          >
            <Box width='100%' display='flex' justifyContent='space-between' alignItems='flex-start'>
              <Button bg={colors.lime}
                    onClick={()=>handleRevertReportDocChanges()}
              >
                Restore this version
              </Button>
              <Button onClick={()=>{
                setIsEditHistoryOn(false); //Close the edit history configuration.
              }}>
                Close
              </Button>
            </Box>
            
            <Box height={heightToBottom - 100}>
              <RichTextEditor
                key={Date.now()}
                content={previousReportDocContent || ""}
                isDisabled={true}
                isToolbarDisabled={true}
                height="100%"
                isBorderRounded={false}
              />
            </Box>
          </Box>
        }
      </Box>
      <Box
        // boxShadow: isOpen ? "0px 4px 10px rgba(0,0,0,0.1)" : "none",
        boxShadow="0px 4px 10px rgba(0,0,0,0.1)"
        width="65%"
        bg="white"
        height="100%"
      >
        <RichTextEditor
          content={reportDoc?.content || ""}
          onChange={onUserEditsCanvas}
          height="100%"
          isBorderRounded={false}
          toolbarRight={<CanvasButtons reportDoc={reportDoc} setIsEditHistoryOn={setIsEditHistoryOn} setPreviousReportDocContent={setPreviousReportDocContent}/>}
          onElementClick={(tagName, domElement, attributes, event) => {
            console.log("onElementClick!", tagName, domElement, attributes, event);
            openSidebar(attributes, domElement);
          }}
        />
      </Box>
    </Flex>
  );
}

const HeaderButtons = () => {
  const roundSelector = useSelector((state) => state.issbRound);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Avoid: copy-paste code (esp early-stage fragile code - we'd end up chasing the same bug twice)
  // Avoid: bad UX (see comment below on the button)
  // The Reports link does what we need.
  //   const createReportDocAndGotoEditor = () => {
  //     // make a stub report doc if none exists, and get an id
  //     const stubReportDoc = { state: LifecycleState.PROCESSING };
  //     reportDocService.create(stubReportDoc).then((newReportDoc) => {
  //       console.log("newReportDoc", newReportDoc.id);
  //       queryClient.invalidateQueries(["reportDocList", roundSelector?.selectedRound]);
  //       navigate(routes.reportDocEditor.replace(":id", newReportDoc.id));
  //     });
  //   };

  return (
    <Flex my={4} alignItems="center" justifyContent="space-between">
      <AButton leftIcon={<FaArrowLeft />} variant="link" color="limeShade.600" href={routes.issbReportDoc}>
        <Text as="span" color="whiteShade.600">
          Reports
        </Text>
      </AButton>
      {/* broken UX: feels like its part of the editor controls, but it removes you from the editor. <Box onClick={() => createReportDocAndGotoEditor()} cursor="pointer">
        <PlusCircle color={colors.limeShade[600]} size={24} />
      </Box> */}
    </Flex>
  );
};

/**
 *
 * @param {Object} props
 * @param {ReportDoc} props.reportDoc
 * @returns
 */
function SourcesAndFilters({ reportDoc }) {
  const { getAccessTokenSilently } = useAuth();
  const { data: sources, isLoading: isLoadingSources } = useQuery({
    queryKey: ["sources", reportDoc?.id],
    queryFn: async () => {
      await getAccessTokenSilently();
      return questionnaireService.list({ ids: reportDoc.sourcesAllowed.map(baseIdFromTypedId) });
    },
    enabled: !!reportDoc?.sourcesAllowed?.length,
  });
  // TODO on-click show sources / filters in the right-hand panel
  // For sources sidebar
  const sourcesSidebar = useRightSidebar();
  const openSourcesSidebar = () => {
    sourcesSidebar.setRightSidebar(<SourcesDetailsPanel sources={sources} count={reportDoc?.sourcesAllowed?.length} />);
    console.log(sources);
    sourcesSidebar.doOpen();
  };

  // For filters sidebar
  const conditions = useMemo(
    () => (reportDoc?.questionFilter ? SearchString.parse(reportDoc?.questionFilter).getConditionArray() : []),
    [reportDoc?.questionFilter]
  );
  const filterKeys = reportDoc?.questionFilter ? [...new Set(conditions.map((c) => c.keyword))] : [];
  const filterSidebar = useRightSidebar();
  const openFilterSidebar = () => {
    filterSidebar.setRightSidebar(<FiltersDetailsPanel filters={conditions} count={conditions.length} />);
    console.log("condition => ", conditions);
    console.log("filterKeys => ", filterKeys);
    // console.log(filterList);
    filterSidebar.doOpen();
  };

  return (
    <Box sx={{ mt: 5, mb: 5, display: "flex", gap: 2 }}>
      <Button
        fontSize="body.s"
        backgroundColor={`${colors.teal}`}
        color={colors.pureWhite}
        sx={{
          display: "flex",
          gap: 2,
        }}
        onClick={openSourcesSidebar}
      >
        <Files size={18} />
        Sources: {reportDoc?.sourcesAllowed?.length}
      </Button>
      {reportDoc?.questionFilter && (
        <Button
          size="md"
          backgroundColor={`${colors.teal}`}
          color={colors.pureWhite}
          sx={{
            display: "flex",
            gap: 2,
          }}
          onClick={openFilterSidebar}
        >
          <FadersHorizontal size={"24px"} />
          Filters: {"" + conditions.length}
        </Button>
      )}
    </Box>
  );
}

// Utility component for Sources display in right sidebar.
function SourcesDetailsPanel({ sources, count }) {
  return (
    <Box
      sx={{
        width: "100%",
        height: "auto",
        padding: 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-evenly",
        alignItems: "flex-start",
      }}
    >
      <RightSidebarHeader>Sources: {count}</RightSidebarHeader>
      {sources && sources.content.map((source) => <Box key={source.id}>- {source.name}</Box>)}
    </Box>
  );
}

// Utility component for filters display in right sidebar.
function FiltersDetailsPanel({ filters, count }) {
  return (
    <Box
      sx={{
        width: "100%",
        height: "auto",
        padding: 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-evenly",
        alignItems: "flex-start",
      }}
    >
      <RightSidebarHeader>Filters: {count}</RightSidebarHeader>
      {filters && filters.map((filter, index) => <Box key={index}>- {filter.keyword + ` (${filter.value}) `}</Box>)}
    </Box>
  );
}

/**
 * Copy, share, Future: download
 
* Note: download is in figma, but was discussed with Jeremy and excluded from v1
 * @param {*} param0
 */
function CanvasButtons({ reportDoc, setIsEditHistoryOn, setPreviousReportDocContent }) {
  const toast = useToast();
  const navigate = useNavigate();

  const doCopyToClipboard = () => {
	// remove cite tags (which would be meaningless in another app) 
	// Note: remove any surrounding whitespace to clean up "blah <cite>[1]</cite>." resulting in an orphaned full-stop
	const contentWithoutCites = reportDoc?.content.replace(/\s*<cite[^>]*>\[\d+\]<\/cite>\s*/g, "");
    copyToClipboard(contentWithoutCites);
    showSuccessToast(toast, "Copied to clipboard", "copy-to-clipboard");
  };

  const doShare = () => {
    navigator.clipboard.writeText(window.location.href);
    showSuccessToast(toast, "Url copied to clipboard. You can share this within your organization.");
  };

  const { confirm } = useConfirmation();
  const queryClient = useQueryClient();
  const doDelete = async () => {
    const confirmed = await confirm({
      title: "Delete Report " + reportDoc?.title || "",
      message: "Are you sure you want to delete this report?",
      confirmText: "Delete",
      cancelText: "Cancel",
    });
    if (!confirmed) return;
    reportDocService.delete(reportDoc.id).then(() => {
      showSuccessToast(toast, "Report doc deleted", "report-doc-deleted");
      queryClient.invalidateQueries({ queryKey: ["reportDoc"] });
      navigate(routes.issbReportDoc);
    });
  };

  // use the right sidebar to show the history
  const { setRightSidebar, doOpen, isOpen, doClose, activationId } = useRightSidebar();
  const showHistory = () => {
    setRightSidebar(<DocReportHistoryPanel reportDoc={reportDoc} setIsEditHistoryOn={setIsEditHistoryOn} setPreviousReportDocContent={setPreviousReportDocContent}/>);
    doOpen();
  };

  return (
    <Box display="flex" gap={1} flexDirection="row" alignItems="center">
      <ReportDocStateToggle reportDoc={reportDoc} />

      <Button variant="ghost" size="sm" onClick={doCopyToClipboard}>
        <Copy size={24} color={colors.navIconLime} />
      </Button>
      <Button variant="ghost" size="sm" onClick={doShare}>
        <ShareFat size={24} color={colors.navIconLime} />
      </Button>
	<Button variant="ghost" size="sm" onClick={() => showHistory()}>
		<ClockCounterClockwise size={24} color={colors.navIconLime} />
	</Button>
      <Button variant="ghost" size="sm" onClick={doDelete}>
        <Trash size={24} color={colors.esg_red} />
      </Button>
    </Box>
  );
}

const label4state = {
  [LifecycleState.DRAFT]: "Draft",
  [LifecycleState.ACTIVE]: "Published",
  [LifecycleState.PROCESSING]: "Processing",
};

export function ReportDocStateToggle({ reportDoc, task, thread }) {
  const doChangeState = (_field, value) => {
    reportDoc.state = value;
    return reportDocService.update(reportDoc.id, reportDoc);
  };
  // HACK if task=failed set reportDoc.state to error
  if (task?.status === "failed") {
	if (reportDoc?.state === LifecycleState.PROCESSING) {
      reportDoc.state = LifecycleState.ERROR;	
	}	
  }  
//   const userMessageCount = thread?.messages?.filter(message => message.role === "user").length;
  const assistantMessageCount = thread?.messages?.filter(message => message.role === "assistant").length;
  const toolResultMessageCount = thread?.messages?.filter(message => message.role === "tool_result").length;

  return (<Box display="flex" flexDirection="row" gap={2}>
    <StatusToggle
	  title={task?.status === "failed" ? task.log : thread? thread.status : undefined}
      field="state"
      label={false}
      status={reportDoc?.state}
	  taskStatus={task?.status}
      values={[LifecycleState.DRAFT, LifecycleState.ACTIVE]}
      handleChange={doChangeState}
      valueLabeller={(s) => label4state[s] || toTitleCase(s)}
      colorSchemeForValue={{
        [LifecycleState.ACTIVE]: "green",
        [LifecycleState.DRAFT]: "gray",
        [LifecycleState.PROCESSING]: "orange",
		[LifecycleState.ERROR]: "red"
      }}
    />
	{reportDoc?.state === LifecycleState.PROCESSING && thread && <DevOnly>
		<SmallText>{assistantMessageCount} AI outputs, {toolResultMessageCount} tool results. Status: {displayThreadStatus(thread.status)}</SmallText>
	</DevOnly>}
  </Box>);
} // ./CanvasButtons

function DocReportHistoryPanel({ reportDoc, setIsEditHistoryOn, setPreviousReportDocContent }) {
  // TODO load history
  // NB useEffect rather than useQuery as we expect changes so we dont want to cache this
  const [diffsPage, setDiffsPage] = useState(null);
  useEffect(() => {
    reportDocService.getHistory(reportDoc.id).then((diffsPage) => {
      setDiffsPage(diffsPage);
    });
  }, [reportDoc.id]);
  const diffs = diffsPage?.content || [];
  const sortedDiffs = diffs.sort((a, b) => { return (new Date(b.createdDate) - new Date(a.createdDate)) });
  console.log('diffs => ',diffs);
  // TODO show items with: click to view this version, button to revert to this version

  return (
    <Box>
      <RightSidebarHeader>Edit History</RightSidebarHeader>
      {diffs.map((diff) => (
        <HistoryPanelEditTile key={diff.id} editDetails={diff} setIsEditHistoryOn={setIsEditHistoryOn} setPreviousReportDocContent={setPreviousReportDocContent}/>
      ))}
    </Box>
  );
}

// THIS FUNCTION CREATED THE EDIT HISTORY TILE INSIDE THE RIGHT SIDEBAR.
function HistoryPanelEditTile({ editDetails, setIsEditHistoryOn, setPreviousReportDocContent }){
  // To  fetch the username from the userID obtained from edit object.
  const { data: userName } = useQuery({
    queryKey: ["userName", editDetails.createdBy],
    queryFn: () => api.getUserById(editDetails.createdBy).then((user) => user.username),
    enabled: !!editDetails.createdBy,
  });
  const contentEdit = editDetails.jsonPatch.find(edit => edit.path === "/content");

  const onClick = () => {	
	// To set the previous report doc version.
		// NOTE: Do not user the reportDoc parameter as it contains wrong data.
	// instead use the jsonPath value variable to get the changes.
	// If this is NOT an op replace content change - do nothing
	if ( ! contentEdit) {
		console.log("NOT an op replace content change - do nothing", editDetails.jsonPatch[0]);
		return;
	}
	setIsEditHistoryOn(true); // change page layout and replace AI widget with previous version editor.
	const previousReportDocContent = contentEdit.value;
	setPreviousReportDocContent(previousReportDocContent);	
  }

  return(
    <Card sx={{ m: 3, p: 5, display:'flex', flexDirection:'column', justifyContent:'flex-start',
              alignItems:'flex-start', gap: 2
     }}
	 	style={{ cursor: contentEdit ? "pointer" : "default" }}
          onClick={onClick}
    >
      <Text>{formatDate(editDetails.createdDate)}</Text>
      {!editDetails.byAI && <Text>{`Edited by: ${userName}`}</Text>}
      <Text>{editDetails.byAI ? "Edited By: AI" : "human"}</Text>
    </Card>
  );
}
